// FILE: src/components/admin/content.js
// The course content model on the admin side (WP-5; R1, matrix 0.7, 0.9,
// 3.7, 9.7): form ⇄ API conversions for lessons (resources), the same
// limits the server checks (src/services/content.js in the backend), and
// the server's validation problems ({ field: "slides[0].slideTime", problem })
// turned into words next to the right field. Pages never show the server's
// problem text or a field path: they show problemMessage(field, problem).
import { formatDuration } from "../../utils/timeFormat";

export const LIMITS = {
  title: 200,
  description: 5000,
  url: 500,
  slides: 2000,
  slideName: 300,
  slideTimeSec: 36000,
  ccCode: 64,
  ilos: 200,
  media: 200,
  order: 10000,
  curriculumText: 200
};
/** MTO 3.5: each video ≤ 7 minutes. */
export const VIDEO_MAX_SEC = 7 * 60;
/** CD3.1: 20 hours of core digital content. */
export const CORE_MINUTES_REQUIRED = 1200;
/** MTO Preface: at most three curricula approved at once. */
export const MAX_APPROVED = 3;

// Same rule as the server: letters, digits and . _ - / (e.g. U1.L2.ILO3).
export const CC_CODE_RE = /^[A-Za-z0-9][A-Za-z0-9._\-/]*$/;
export const CHALLENGE_TYPES = ["NONE", "INTERACTIVE", "QUIZ", "MATCHING"];

const n0 = (n) => Number(n).toLocaleString("en-CA");

/* ------------------------------------------------------------ field paths */

/** "slides[3].slideTime" → { list: "slides", index: 3, key: "slideTime" }. */
export function parseFieldPath(path) {
  const s = String(path || "");
  const m = /^(slides|media|ilos)\[(\d+)\](?:\.(\w+))?$/.exec(s);
  if (m) return { list: m[1], index: Number(m[2]), key: m[3] || m[1] };
  return { list: null, index: null, key: s };
}

const FIELD_LABELS = {
  title: "Title",
  description: "Description",
  resourceType: "Type",
  kind: "Kind of lesson",
  order: "Position",
  url: "Content file",
  parentResourceId: "Parent chapter",
  isPractice: "Practice activity",
  assessment: "Pass mark",
  "assessment.passMark": "Pass mark",
  ilos: "Learning outcomes",
  curriculumId: "Curriculum",
  slides: "Slides",
  media: "Videos",
  slideName: "name",
  slideTime: "time",
  pageIndex: "page",
  challengeType: "activity",
  ccCode: "CC code",
  durationSec: "length",
  slideIndex: "slide number",
  name: "Name",
  mtoApprovalRef: "MTO approval reference"
};

/** Words for a field path: "Slide 1: time", "Video 2: length", "Pass mark". */
export function fieldLabel(path) {
  const { list, index, key } = parseFieldPath(path);
  if (list === "slides") return `Slide ${index + 1}${key === "slides" ? "" : `: ${FIELD_LABELS[key] || "value"}`}`;
  if (list === "media") return `Video ${index + 1}${key === "media" ? "" : `: ${FIELD_LABELS[key] || "value"}`}`;
  if (list === "ilos") return `Learning outcome ${index + 1}`;
  return FIELD_LABELS[key] || "A field";
}

const CC_HINT = "Use a CC code made of letters, digits, dots, dashes, underscores or slashes, like U1.L2.ILO3.";

/**
 * Plain-language message for one problem. The server's `problem` text is
 * only used to tell cases apart, never shown.
 */
export function problemMessage(path, problem = "") {
  const p = String(problem || "").toLowerCase();
  const { list, key } = parseFieldPath(path);
  if (p === "unknown field") return "This value can't be saved here.";
  if (list === "ilos") return CC_HINT;
  switch (key) {
    case "title":
      return p.includes("long") ? `Use ${n0(LIMITS.title)} characters or fewer.` : "Enter a title.";
    case "name":
      return p === "required" ? "Enter a name." : `Use 1 to ${n0(LIMITS.curriculumText)} characters.`;
    case "mtoApprovalRef":
      return p === "required" ? "Enter the reference from the MTO approval." : `Use 1 to ${n0(LIMITS.curriculumText)} characters.`;
    case "description":
      return `Use ${n0(LIMITS.description)} characters or fewer.`;
    case "resourceType":
      return "Choose chapter or homelink.";
    case "kind":
      return "A chapter is lesson content or a graded assessment; a homelink is always a homelink.";
    case "order":
      return `Enter a whole number from 1 to ${n0(LIMITS.order)}.`;
    case "url":
      return "Enter the lesson's file in the content store, like chapters/ch_1/story.html (not a web address, no slash at the start).";
    case "parentResourceId":
      if (p.includes("only homelinks")) return "Only homelinks and graded assessments belong to a chapter.";
      if (p.includes("same curriculum")) return "Choose a chapter from the same curriculum.";
      if (p.includes("content chapter")) return "Choose a content chapter, not an assessment.";
      if (p.includes("itself")) return "A lesson can't belong to itself.";
      if (p.includes("no such")) return "That chapter no longer exists. Choose another one.";
      return "Choose a chapter from the list.";
    case "isPractice":
      return "Choose whether this is a practice activity.";
    case "assessment":
    case "passMark":
    case "assessment.passMark":
      return "Enter a pass mark from 1 to 100 %, or leave it empty.";
    case "ilos":
      return p.includes("at most") ? `Use at most ${n0(LIMITS.ilos)} learning outcomes.` : CC_HINT;
    case "curriculumId":
      return p === "required" ? "Choose the draft curriculum this lesson belongs to." : "Choose a draft curriculum from the list.";
    case "slides":
      return p.includes("at most") ? `A lesson can have at most ${n0(LIMITS.slides)} slides.` : "Check the slides.";
    case "slideName":
      return p.includes("long") ? `Use ${n0(LIMITS.slideName)} characters or fewer.` : "Enter the slide's name.";
    case "slideTime":
      return `Enter the time in seconds, from 0 to ${n0(LIMITS.slideTimeSec)}.`;
    case "pageIndex":
      return "Enter a whole number, 1 or more.";
    case "challengeType":
      return "Choose the activity on this slide.";
    case "ccCode":
      return CC_HINT;
    case "media":
      return p.includes("at most") ? `A lesson can have at most ${n0(LIMITS.media)} videos.` : "Check the videos.";
    case "durationSec":
      return `Enter the video's length in seconds, more than 0 (at most ${n0(LIMITS.slideTimeSec)}).`;
    case "slideIndex":
      return p.includes("no such") ? "This lesson has no slide with that number." : "Enter a slide number, 1 or more, or leave it empty.";
    default:
      return "Check this value.";
  }
}

/** [{ field, problem }] → { [field]: message } (first problem per field wins). */
export function problemsByField(problems = []) {
  const out = {};
  for (const pr of problems || []) {
    if (!pr || !pr.field || out[pr.field]) continue;
    out[pr.field] = problemMessage(pr.field, pr.problem);
  }
  return out;
}

/** The server's problems as "Slide 1: time: …" lines for an error summary. */
export function problemList(problems = []) {
  const seen = new Set();
  return (problems || [])
    .filter((pr) => pr && pr.field && !seen.has(pr.field) && seen.add(pr.field))
    .map((pr) => ({ field: pr.field, label: fieldLabel(pr.field), message: problemMessage(pr.field, pr.problem) }));
}

/* ------------------------------------------------------------ lesson details */

const isBlank = (v) => v === undefined || v === null || String(v).trim() === "";
const WHOLE = /^\d+$/;
const NUMBER = /^\d+(\.\d+)?$/;

/** Pass mark fraction (0.8) → percent text ("80"). */
export function percentText(passMark) {
  if (passMark === null || passMark === undefined || passMark === "") return "";
  const n = Number(passMark);
  return Number.isFinite(n) ? String(Math.round(n * 10000) / 100) : "";
}

/** Pass mark fraction → "80 %" or "—". */
export function passMarkText(passMark) {
  const t = percentText(passMark);
  return t ? `${t} %` : "—";
}

/** The CC codes on a lesson's slides, in slide order. */
export function slideCodes(slides = []) {
  return [...new Set((slides || []).map((s) => s?.ccCode).filter(Boolean))];
}

export function splitCodes(text) {
  return [
    ...new Set(
      String(text || "")
        .split(/[\s,;]+/)
        .map((c) => c.trim())
        .filter(Boolean)
    )
  ];
}

/** A resource (or nothing, for a new lesson) → the details form. */
export function detailsForm(resource, defaults = {}) {
  const r = resource || {};
  const fromSlides = slideCodes(r.slides);
  return {
    curriculumId: r.curriculumId ? String(r.curriculumId) : defaults.curriculumId || "",
    resourceType: r.resourceType || defaults.resourceType || "CHAPTER",
    kind: r.kind || (r.resourceType === "HOMELINK" ? "HOMELINK" : "CONTENT"),
    title: r.title || "",
    description: r.description || "",
    order: r.order === undefined || r.order === null ? "" : String(r.order),
    url: r.url || "",
    parentResourceId: r.parentResourceId ? String(r.parentResourceId) : "",
    isPractice: !!r.isPractice,
    passMark: percentText(r.assessment?.passMark),
    // Only the outcomes not already on a slide: slide codes are added by the server.
    ilos: (r.ilos || []).filter((c) => !fromSlides.includes(c)).join(", ")
  };
}

/** Homelinks and graded assessments belong to a content chapter (D2, D3). */
export function hasParent(form) {
  return form.resourceType === "HOMELINK" || form.kind === "ASSESSMENT";
}

/** Checks the details form with the server's rules; problems use the server's field paths. */
export function validateDetails(form, { create = false, needsCurriculum = false } = {}) {
  const problems = [];
  const add = (field, problem) => problems.push({ field, problem });
  if (create && needsCurriculum && isBlank(form.curriculumId)) add("curriculumId", "required");
  if (isBlank(form.title)) add("title", "required");
  else if (form.title.trim().length > LIMITS.title) add("title", "too long");
  if ((form.description || "").length > LIMITS.description) add("description", "too long");
  const order = String(form.order).trim();
  if (!WHOLE.test(order) || Number(order) < 1 || Number(order) > LIMITS.order) add("order", "integer");
  const url = String(form.url || "").trim();
  if (
    url &&
    (url.length > LIMITS.url ||
      /^[a-z][a-z0-9+.-]*:/i.test(url) ||
      url.startsWith("/") ||
      url.includes("\\") ||
      url.split("/").some((s) => s === "" || s === "." || s === ".."))
  ) {
    add("url", "content key");
  }
  if (!form.isPractice && !isBlank(form.passMark)) {
    const t = String(form.passMark).trim();
    const v = Number(t);
    if (!NUMBER.test(t) || !(v > 0 && v <= 100)) add("assessment.passMark", "fraction");
  }
  const codes = splitCodes(form.ilos);
  if (codes.length > LIMITS.ilos) add("ilos", "at most");
  codes.forEach((c, i) => {
    if (c.length > LIMITS.ccCode || !CC_CODE_RE.test(c)) add(`ilos[${i}]`, "invalid CC code");
  });
  return problems;
}

/** The details form → the request body (POST on create, PATCH on edit). */
export function detailsPayload(form, { create = false } = {}) {
  const homelink = form.resourceType === "HOMELINK";
  const kind = homelink ? "HOMELINK" : form.kind === "ASSESSMENT" ? "ASSESSMENT" : "CONTENT";
  const passMark = form.isPractice || isBlank(form.passMark) ? null : Number((Number(form.passMark) / 100).toFixed(4));
  const body = {
    title: form.title.trim(),
    description: (form.description || "").trim(),
    kind,
    order: Number(String(form.order).trim()),
    url: String(form.url || "").trim() || null,
    parentResourceId: homelink || kind === "ASSESSMENT" ? form.parentResourceId || null : null,
    isPractice: !!form.isPractice,
    assessment: { passMark },
    ilos: splitCodes(form.ilos)
  };
  if (create) {
    body.resourceType = form.resourceType;
    if (form.curriculumId) body.curriculumId = form.curriculumId;
  }
  return body;
}

/* ------------------------------------------------------------ slides and videos */

let rowKey = 0;
const nextKey = () => `row-${++rowKey}`;

export function slideRows(slides = []) {
  return (slides || []).map((s, i) => ({
    key: nextKey(),
    slideName: s.slideName || "",
    slideTime: s.slideTime === undefined || s.slideTime === null ? "0" : String(s.slideTime),
    pageIndex: s.pageIndex ? String(s.pageIndex) : String(i + 1),
    challengeType: CHALLENGE_TYPES.includes(s.challengeType) ? s.challengeType : "NONE",
    ccCode: s.ccCode || ""
  }));
}

export function newSlideRow(position) {
  return { key: nextKey(), slideName: "", slideTime: "", pageIndex: String(position), challengeType: "NONE", ccCode: "" };
}

export function mediaRows(media = []) {
  return (media || []).map((m) => ({
    key: nextKey(),
    title: m.title || "",
    durationSec: m.durationSec === undefined || m.durationSec === null ? "" : String(m.durationSec),
    slideIndex: m.slideIndex ? String(m.slideIndex) : ""
  }));
}

export function newMediaRow() {
  return { key: nextKey(), title: "", durationSec: "", slideIndex: "" };
}

/** Checks the slides and videos with the server's rules and field paths. */
export function validateSlides(slides = [], media = []) {
  const problems = [];
  const add = (field, problem) => problems.push({ field, problem });
  if (slides.length > LIMITS.slides) add("slides", "at most");
  slides.forEach((s, i) => {
    const name = String(s.slideName || "").trim();
    if (!name) add(`slides[${i}].slideName`, "required");
    else if (name.length > LIMITS.slideName) add(`slides[${i}].slideName`, "too long");
    const t = String(s.slideTime).trim();
    if (!NUMBER.test(t) || Number(t) > LIMITS.slideTimeSec) add(`slides[${i}].slideTime`, "seconds");
    const page = String(s.pageIndex).trim();
    if (page && (!WHOLE.test(page) || Number(page) < 1)) add(`slides[${i}].pageIndex`, "integer");
    if (!CHALLENGE_TYPES.includes(s.challengeType)) add(`slides[${i}].challengeType`, "one of");
    const code = String(s.ccCode || "").trim();
    if (code && (code.length > LIMITS.ccCode || !CC_CODE_RE.test(code))) add(`slides[${i}].ccCode`, "invalid CC code");
  });
  if (media.length > LIMITS.media) add("media", "at most");
  media.forEach((m, i) => {
    const title = String(m.title || "");
    if (title.length > LIMITS.title) add(`media[${i}].title`, "string");
    const d = String(m.durationSec).trim();
    if (!NUMBER.test(d) || !(Number(d) > 0) || Number(d) > LIMITS.slideTimeSec) add(`media[${i}].durationSec`, "seconds");
    const at = String(m.slideIndex).trim();
    if (at) {
      if (!WHOLE.test(at) || Number(at) < 1) add(`media[${i}].slideIndex`, "integer");
      else if (Number(at) > slides.length) add(`media[${i}].slideIndex`, "no such slide");
    }
  });
  return problems;
}

export function slidesPayload(slides = [], media = []) {
  return {
    slides: slides.map((s, i) => {
      const page = String(s.pageIndex).trim();
      return {
        slideName: String(s.slideName).trim(),
        slideTime: Number(String(s.slideTime).trim()),
        pageIndex: page ? Number(page) : i + 1,
        challengeType: s.challengeType,
        ccCode: String(s.ccCode || "").trim() || null
      };
    }),
    media: media.map((m) => {
      const at = String(m.slideIndex).trim();
      return { kind: "VIDEO", title: String(m.title || "").trim(), durationSec: Number(String(m.durationSec).trim()), slideIndex: at ? Number(at) : null };
    })
  };
}

/** "9 slides · 1 h 30 min · 3 activities" for a list of slide rows or slides. */
export function slideTotals(slides = []) {
  const count = slides.length;
  const seconds = slides.reduce((t, s) => t + (Number(s.slideTime) || 0), 0);
  const activities = slides.filter((s) => s.challengeType && s.challengeType !== "NONE").length;
  return { count, seconds, activities, text: `${count} ${count === 1 ? "slide" : "slides"} · ${formatDuration(seconds)} · ${activities} ${activities === 1 ? "activity" : "activities"}` };
}

/** 425 → "7 min 5 s". */
export function formatSeconds(value) {
  const s = Math.max(0, Math.round(Number(value) || 0));
  const m = Math.floor(s / 60);
  const rest = s % 60;
  if (!m) return `${rest} s`;
  return rest ? `${m} min ${rest} s` : `${m} min`;
}

/** "Zun BDE, version 2" or a fallback. */
export function curriculumName(c, fallback = "No curriculum") {
  if (!c) return fallback;
  return c.version ? `${c.name}, version ${c.version}` : c.name || fallback;
}
